// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { Link } from '@/components/link';
import { ShippingCarrierTrackingLabel } from '@/components/shipping-carrier-tracking-label';
import { ArrowForward } from '@/constants/hardfin-standard-icons';
import { ShippingCarrier } from '@/constants/shipping-carrier';
import { Currency } from '@/currency/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { shipmentHasExtraItems } from '@/fulfillment/components/extra-items';
import { ExtraItemsAlert } from '@/fulfillment/components/extra-items-alert';
import { useFulfillment } from '@/fulfillment/hooks/use-fulfillment';
import { ShipmentDisplayStatus } from '@/shipment/api/types';
import { MissingShipmentChip } from '@/shipment/components/missing-shipment-chip';
import { QuarantineShipmentChip } from '@/shipment/components/quarantine-shipment-chip';
import { ShipmentQuantityCell } from '@/shipment/components/shipment-quantity-cell';
import { ShipmentStatusChip } from '@/shipment/components/shipment-status-chip';
import { useShipments } from '@/shipment/hooks/use-shipments';
import { outboundPlannedQuantity } from '@/shipment/utilities/shipment-planned-quantity';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

export const Route = createFileRoute(
  '/_app/fulfillments/$fulfillmentKey/shipments'
)({
  component: FulfillmentShipmentsTab,
});

/**
 * Renders the fulfillment shipments tab for a specific fulfillment order.
 */
export function FulfillmentShipmentsTabContent(props: {
  fulfillmentKey: string;
}) {
  const { fulfillmentKey } = props;
  const { fulfillment } = useFulfillment(fulfillmentKey);
  const { shipments } = useShipments({
    filter: { foNumbers: [fulfillment.fulfillmentOrderNumber] },
  });

  // Build a set of item IDs from FO line items for detecting extra shipment items
  const foItemIds = useMemo(
    () => new Set(fulfillment.items.map((item) => item.item.id)),
    [fulfillment.items]
  );

  // Transform shipments into flat rows with product/part information
  // Only include shipment items that match FO line items
  const shipmentRows = useMemo(() => {
    const rows: {
      shipmentId: string;
      shipmentKey: string;
      shipmentNumber: string;
      status: ShipmentDisplayStatus;
      hasExtraItems: boolean;
      missingSince: DateTime | null;
      thirdPartyBound: boolean;
      partSku: string;
      partName: string;
      quantity: number;
      receivedQuantity: number | null;
      quarantinedQuantity: number;
      shippedAt: DateTime | null;
      deliveredAt: DateTime | null;
      carrier: ShippingCarrier | null;
      trackingNumber: string | null;
      shippingCost: Currency | null;
    }[] = [];

    shipments.forEach((shipment) => {
      const hasExtraItems = shipmentHasExtraItems(shipment, foItemIds);

      // Add a row for each item in the shipment that matches an FO line item
      shipment.items.forEach((item) => {
        const shouldInclude = foItemIds.has(item.id);
        const partSku = item.item.sku || '';
        const partName = item.item.name;

        // Only add the row if this shipment item matches an FO line item
        if (shouldInclude) {
          rows.push({
            shipmentId: shipment.id,
            shipmentKey: shipment.key,
            shipmentNumber: shipment.shipmentNumber,
            status: shipment.status,
            hasExtraItems,
            missingSince: shipment.missingSince,
            thirdPartyBound: shipment.thirdPartyBound,
            partSku,
            partName,
            quantity: item.quantity ?? 0,
            receivedQuantity: item.receivedQuantity,
            quarantinedQuantity: item.quarantinedQuantity,
            shippedAt: shipment.shippedAt,
            deliveredAt: shipment.deliveredAt,
            carrier: shipment.trackingNumberDetails?.carrier ?? null,
            trackingNumber:
              shipment.trackingNumberDetails?.trackingNumber ?? null,
            shippingCost: shipment.shippingCost,
          });
        }
      });
    });

    // Sort by ship date descending (most recent first), with shipment ID as secondary sort for stability
    rows.sort((a, b) => {
      const aDate = a.shippedAt?.toMillis() ?? 0;
      const bDate = b.shippedAt?.toMillis() ?? 0;

      // Sort by ship date descending
      if (bDate !== aDate) {
        return bDate - aDate;
      }

      // Secondary sort by shipment ID for stable sort
      return a.shipmentId.localeCompare(b.shipmentId);
    });

    return rows;
  }, [shipments, foItemIds]);

  return (
    <HardfinTabCardWrapper title="Shipments">
      <Box paddingY={2}>
        {shipmentRows.length === 0 ? (
          <Box padding={3}>
            <Typography variant="body2" color="text.secondary">
              No shipments
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Shipment ID</TableCell>
                  <TableCell>Part</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Shipping cost</TableCell>
                  <TableCell>Ship date</TableCell>
                  <TableCell>Delivery date</TableCell>
                  <TableCell>Tracking</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipmentRows.map((row, index) => (
                  <TableRow key={`${row.shipmentId}-${index}`}>
                    <TableCell>
                      <ShipmentStatusChip status={row.status} />
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Link
                            to="/shipments/$shipmentKey"
                            params={{ shipmentKey: row.shipmentKey }}
                            search={{ type: 'outbound' }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            {row.shipmentNumber}
                            <ArrowForward fontSize="small" />
                          </Link>
                          {row.hasExtraItems && <ExtraItemsAlert />}
                          <MissingShipmentChip
                            missingSince={row.missingSince}
                            thirdPartyBound={row.thirdPartyBound}
                          />
                        </Stack>
                        <QuarantineShipmentChip
                          openCount={row.quarantinedQuantity}
                          shipmentKey={row.shipmentKey}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.partSku}{' '}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          ({row.partName})
                        </Typography>
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <ShipmentQuantityCell
                        plannedQuantity={
                          outboundPlannedQuantity(
                            row.quantity,
                            row.quarantinedQuantity
                          ) ?? 0
                        }
                        receivedQuantity={row.receivedQuantity}
                        isReceived={false}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {row.shippingCost
                        ? formatCurrencyValue(row.shippingCost)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.shippedAt
                        ? row.shippedAt.toLocaleString({ dateStyle: 'medium' })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.deliveredAt
                        ? row.deliveredAt.toLocaleString({
                            dateStyle: 'medium',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <ShippingCarrierTrackingLabel
                        carrier={row.carrier}
                        trackingIdentifier={row.trackingNumber}
                        linkNumberDirectly={true}
                        variant="body2"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </HardfinTabCardWrapper>
  );
}

/**
 * Resolves the route params and renders the shipments tab.
 */
function FulfillmentShipmentsTab() {
  const { fulfillmentKey } = Route.useParams();

  return <FulfillmentShipmentsTabContent fulfillmentKey={fulfillmentKey} />;
}
